import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Home } from "lucide-react";

/**
 * Branded 404 — same tone of voice as the marketing site.
 * Shown when a PUBLIC visitor hits an unknown URL; authenticated users
 * bounce to their portal via <RoleRedirect> instead.
 */
export default function NotFound() {
    return (
        <div
            data-testid="not-found-page"
            className="min-h-screen bg-cream text-navy-700 relative overflow-hidden"
        >
            {/* subtle grain + gold arcs so it doesn't feel flat */}
            <div className="pointer-events-none absolute inset-0 opacity-[0.05]"
                style={{
                    backgroundImage:
                        "radial-gradient(circle at 20% 10%, #001c50 1px, transparent 1px), radial-gradient(circle at 80% 60%, #D4AF37 1px, transparent 1px)",
                    backgroundSize: "42px 42px, 68px 68px",
                }}
            />

            <div className="relative max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
                <div>
                    <div className="text-[10px] tracking-[0.35em] uppercase text-gold-600 mb-4">
                        Error 404 · Panda took a scoop-break
                    </div>
                    <h1 className="font-display text-5xl lg:text-7xl text-navy-700 leading-[1.05]">
                        Nothing here <em className="italic text-gold-600 not-italic">but sprinkles.</em>
                    </h1>
                    <p className="mt-6 text-navy-500 text-lg leading-relaxed max-w-lg">
                        Our panda got distracted by an ice-cream and dropped the page you were looking for.
                        We&apos;ve given him a stern look. Meanwhile, let&apos;s get you back to something delicious.
                    </p>

                    <div className="mt-10 flex flex-wrap gap-3">
                        <Button
                            asChild
                            className="rounded-full bg-navy-700 hover:bg-navy-800 text-white px-6"
                            data-testid="not-found-home"
                        >
                            <Link to="/">
                                <Home size={16} className="mr-2" /> Back to Perx
                            </Link>
                        </Button>
                        <Button
                            asChild
                            variant="ghost"
                            className="rounded-full text-navy-700 hover:bg-navy-50 px-6"
                            data-testid="not-found-browse"
                        >
                            <Link to="/browse">
                                <ArrowLeft size={16} className="mr-2" /> Browse the deals
                            </Link>
                        </Button>
                    </div>

                    <div className="mt-12 text-xs text-navy-400 tracking-wider">
                        If you were looking for something specific, drop us a line via the{" "}
                        <Link to="/#contact" className="underline decoration-gold-500 underline-offset-4">
                            Perx concierge form
                        </Link>
                        {" "}and we&apos;ll dig it up.
                    </div>
                </div>

                <div className="relative">
                    <div className="absolute -inset-6 bg-gold-100 rounded-[36px] rotate-[-3deg] opacity-70" />
                    <div className="relative rounded-[32px] bg-white shadow-[0_30px_80px_-40px_rgba(0,28,80,0.35)] p-6 lg:p-10">
                        <img
                            src="/panda/panda-icecream.png"
                            alt="Panda enjoying an ice-cream"
                            className="w-full h-auto rounded-2xl select-none"
                            draggable={false}
                        />
                        <div className="mt-6 text-center">
                            <div className="font-display text-2xl text-navy-700">
                                &ldquo;My bad.&rdquo;
                            </div>
                            <div className="text-xs text-gold-600 tracking-[0.25em] uppercase mt-2">
                                — Panda, distracted
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
